'use client'

import { useMemo } from 'react'
import { useSession } from 'next-auth/react'
import { pathOr } from 'ramda'

import {
  CustomerTypes,
  extractCustomerFromClientSession
} from 'modules/customer'
import { getProductUnitDetails } from 'modules/products'
import { ProductSeoStorage, SeoListName } from 'shared/analytics'

import { removeFromCartSeo } from '../../../analytics'
import useCartStore from '../../../store/cartStore'
import { CartItem } from '../../../types'

const useCartItemView = (item: CartItem, listName: SeoListName) => {
  const { product } = item
  const productId = product.id
  const session = useSession()
  const user: CustomerTypes | undefined = extractCustomerFromClientSession(session)
  const { getProductCount, cart } = useCartStore()

  const productDetails = useMemo(() => {
    const promoUnitPrice: string | null = item?.promotionId
      ? item?.priceGross.formatted
      : item?.specialOfferPriceGross?.formatted || null

    const totalPrice: string | null = item?.valueGross?.formatted || '---'
    const promoTotalPrice: string | null =
      item?.specialOfferValueGross?.formatted || null
    const unitDetails = getProductUnitDetails(product)
    const unitPrice = product.pricing?.priceGross.formatted || '---'
    const hasWeeklyAvailabilityPlan: boolean =
      product?.hasAvailabilityPlan || false
    const weeklyAvailabilityPlan = product?.availabilityPlan || null
    const productPath = product?.slug || ''
    const productImage = pathOr(
      '/static/photo-placeholder.svg',
      ['files', 0, 'thumbnailPathSmall'],
      product
    )
    const unitPerPrice: string = product.mainUnit?.name || ''

    return {
      product,
      promoUnitPrice,
      totalPrice,
      promoTotalPrice,
      unitDetails,
      unitPrice,
      hasWeeklyAvailabilityPlan,
      weeklyAvailabilityPlan,
      productPath,
      productImage,
      unitPerPrice
    }
  }, [product, item])

  // Handle removal from cart with analytics
  const handleRemoveFromCartSeo = () => {
    removeFromCartSeo({
      user,
      product: item.product,
      button_place: 'cart',
      idxOnList: 0, // IN CART INDEX DOES NOT MATTER
      productQuantity: getProductCount(productId),
      cart,
      listName,
      type: 'remove'
    })
  }
  const creativeProductData = useMemo(() => {
    //no usage data if product is not available
    if (!item.isAvailable) return undefined

    return ProductSeoStorage.find(productId)
  }, [productId, item.isAvailable])

  return {
    user,
    handleRemoveFromCartSeo,
    creativeProductData,
    productDetails
  }
}

export default useCartItemView
